import React, { useMemo } from 'react'
import { useQuery } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

import ChartCard from './components/ChartCard'
import ModuleAudienceCard from './components/ModuleAudienceCard'
import Section from './components/Section'
import StatTilesCard from './components/StatTilesCard'
import { AXIS_TICK, CHART_COLORS, TOOLTIP_STYLE } from './constants'
import { OPERATION_PRODUCT_ANALYTICS_EVENT_WIFI } from './operations-gql/queries'
import { EventWifiActivity, EventWifiQuery, ProductAnalyticsPeriod, ProductAnalyticsVariables } from './types'

type EventWifiSectionProps = {
  period: ProductAnalyticsPeriod
}

type NetworkAction = keyof Pick<EventWifiActivity, 'created' | 'updated' | 'deleted' | 'activated' | 'deactivated'>

type ActivityTile = {
  key: keyof EventWifiActivity
  labelKey: string
}

const NETWORK_ACTIONS: NetworkAction[] = ['created', 'updated', 'deleted', 'activated', 'deactivated']

const NETWORK_ACTION_LABEL_WIDTH = 88

const PASSPHRASE_TILES: ActivityTile[] = [
  { key: 'passphraseReset', labelKey: 'passphrase_reset' },
  { key: 'passphraseGenerated', labelKey: 'passphrase_generated' },
]

const STATISTICS_TILES: ActivityTile[] = [
  { key: 'statisticsTabOpened', labelKey: 'statistics_tab_opened' },
  { key: 'usageStatisticsOpened', labelKey: 'usage_statistics_opened' },
]

const EventWifiSection: React.FC<EventWifiSectionProps> = ({ period }) => {
  const { t } = useTranslation()
  const { data, loading, error } = useQuery<EventWifiQuery, ProductAnalyticsVariables>(
    OPERATION_PRODUCT_ANALYTICS_EVENT_WIFI,
    { variables: { period }, errorPolicy: 'all' }
  )
  const audience = data?.productAnalytics?.eventWifiAudience
  const activity = data?.productAnalytics?.eventWifiActivity

  const networkActions = useMemo(() => (
    NETWORK_ACTIONS.map(action => ({
      label: t(`stats.product_analytics.event_wifi.network_actions.${action}`),
      value: activity?.[action] ?? 0,
    }))
  ), [activity, t])

  const hasFieldError = (field: unknown) => Boolean(error) && !field

  const toStatTiles = (tiles: ActivityTile[]) => tiles.map(({ key, labelKey }) => ({
    key,
    label: t(`stats.product_analytics.event_wifi.${labelKey}`),
    value: activity?.[key],
  }))

  return (
    <Section
      id="product-analytics-event-wifi"
      title={t('stats.product_analytics.event_wifi.title')}
      columns={{ xs: 1, md: 2, lg: 4 }}
    >
      <ModuleAudienceCard
        description={t('stats.product_analytics.event_wifi.audience_description')}
        audience={audience}
        loading={loading}
        hasError={hasFieldError(audience)}
      />

      <ChartCard
        title={t('stats.product_analytics.event_wifi.network_actions_title')}
        description={t('stats.product_analytics.event_wifi.network_actions_description')}
        loading={loading}
        hasError={hasFieldError(activity)}
        isEmpty={!networkActions.some(({ value }) => value > 0)}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={networkActions} layout="vertical">
            <CartesianGrid horizontal={false} stroke="#F3F4F6" />
            <XAxis type="number" allowDecimals={false} tick={AXIS_TICK} tickLine={false} />
            <YAxis
              type="category"
              dataKey="label"
              width={NETWORK_ACTION_LABEL_WIDTH}
              interval={0}
              tick={AXIS_TICK}
              tickLine={false}
            />
            <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: '#F9FAFB' }} />
            <Bar
              dataKey="value"
              name={t('stats.product_analytics.event_wifi.actions')}
              fill={CHART_COLORS.quaternary}
              radius={[0, 6, 6, 0]}
              barSize={16}
            />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <StatTilesCard
        title={t('stats.product_analytics.event_wifi.passphrases')}
        description={t('stats.product_analytics.event_wifi.passphrases_description')}
        tiles={toStatTiles(PASSPHRASE_TILES)}
        loading={loading}
        hasError={hasFieldError(activity)}
      />

      <StatTilesCard
        title={t('stats.product_analytics.event_wifi.statistics')}
        description={t('stats.product_analytics.event_wifi.statistics_description')}
        tiles={toStatTiles(STATISTICS_TILES)}
        loading={loading}
        hasError={hasFieldError(activity)}
      />
    </Section>
  )
}

export default EventWifiSection
